import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import formIcon from "../../assets/icons/form.png";
import articleIcon from "../../assets/icons/article.svg";
import DocumentCard from "../DocumentCard";
import ApplicationFormModal from "../ApplicationFormModal";
import "../ApplicationFormModal.css";

const DocumentDownload = () => {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Close modal when the mobile menu (Drawer) requests all modals to close
  useEffect(() => {
    const handleCloseAll = () => {
      setModalOpen(false);
      setSelectedService(null);
    };
    window.addEventListener("closeAppModals", handleCloseAll);
    return () => window.removeEventListener("closeAppModals", handleCloseAll);
  }, []);

  // Form configurations for different services
  const formConfigs = {
    "construction-permit": [
      {
        name: "applicantName",
        labelEn: "Applicant's Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Applicant's Contact Number",
        labelMr: "अर्जदाराचा संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Email",
        labelMr: "ईमेल",
        required: true,
        messageEn: "Please enter email",
        messageMr: "कृपया ईमेल प्रविष्ट करा",
      },
      {
        name: "propertyNumber",
        labelEn: "Property Number",
        labelMr: "ज्या मिळकतीत बांधकाम करायचे आहे त्या मिळकतीचा क्रमांक",
        required: true,
        messageEn: "Please enter property number",
        messageMr: "कृपया मिळकतीचा क्रमांक प्रविष्ट करा",
      },
      {
        name: "propertyLocation",
        labelEn: "Is the property in a village or agricultural area?",
        labelMr: "मिळकत गावठाणात आहे कि शेती गटात?",
        required: true,
        messageEn: "Please specify property location type",
        messageMr: "कृपया मिळकतीचे स्थान निर्दिष्ट करा",
      },
      {
        name: "totalPropertyArea",
        labelEn: "Total Area of Property",
        labelMr: "मिळकतीचे एकूण क्षेत्रफळ",
        required: true,
        messageEn: "Please enter total property area",
        messageMr: "कृपया मिळकतीचे एकूण क्षेत्रफळ प्रविष्ट करा",
      },
      {
        name: "constructionArea",
        labelEn: "Total Area to be Constructed",
        labelMr: "बांधकाम करावयाचे एकूण क्षेत्रफळ",
        required: true,
        messageEn: "Please enter construction area",
        messageMr: "कृपया बांधकाम क्षेत्रफळ प्रविष्ट करा",
      },
      {
        name: "architectCertificate",
        type: "file",
        labelEn: "Architect's Certificate in Prescribed Format",
        labelMr: "आर्किटेक्ट चा विहित नमुन्यातील दाखला",
        required: true,
        messageEn: "Please upload architect certificate",
        messageMr: "कृपया आर्किटेक्ट दाखला अपलोड करा",
      },
      {
        name: "constructionPlan",
        type: "file",
        labelEn: "Construction Plan by Architect",
        labelMr: "आर्किटेक्ट यांनी दिलेला बांधकाम नकाशा",
        required: true,
        messageEn: "Please upload construction plan",
        messageMr: "कृपया बांधकाम नकाशा अपलोड करा",
      },
      {
        name: "otherDocuments",
        type: "file",
        labelEn: "Other Required Documents and Application Form",
        labelMr: "इतर आवश्यक कागदपत्रे व विहित नमुन्यातील अर्ज",
        required: true,
        messageEn: "Please upload other required documents",
        messageMr: "कृपया इतर आवश्यक कागदपत्रे अपलोड करा",
      },
    ],
    "death-registration": [
      {
        name: "applicantName",
        labelEn: "Applicant Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "mobileNumber",
        labelEn: "Applicant Mobile No.",
        labelMr: "अर्जदाराचा मोबाईल नं",
        required: true,
        messageEn: "Please enter mobile number",
        messageMr: "कृपया मोबाईल नंबर प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Applicant Email ID",
        labelMr: "अर्जदाराचा इमेल आयडी",
        required: true,
        messageEn: "Please enter email ID",
        messageMr: "कृपया इमेल आयडी प्रविष्ट करा",
      },
      {
        name: "applicantAddress",
        labelEn: "Applicant Address",
        labelMr: "अर्जदाराचा पत्ता",
        required: true,
        messageEn: "Please enter applicant address",
        messageMr: "कृपया अर्जदाराचा पत्ता प्रविष्ट करा",
      },
      {
        name: "dateOfDeath",
        type: "date",
        labelEn: "Date of Death",
        labelMr: "मृत्यु दिनांक",
        required: true,
        messageEn: "Please enter date of death",
        messageMr: "कृपया मृत्यु दिनांक प्रविष्ट करा",
      },
      {
        name: "gender",
        type: "select",
        labelEn: "Gender",
        labelMr: "लिंग",
        options: [
          {
            valueEn: "",
            labelEn: "-- Select --",
            valueMr: "",
            labelMr: "-- निवडा --",
          },
          {
            valueEn: "male",
            labelEn: "Male",
            valueMr: "पुरुष",
            labelMr: "पुरुष",
          },
          {
            valueEn: "female",
            labelEn: "Female",
            valueMr: "स्त्री",
            labelMr: "स्त्री",
          },
          {
            valueEn: "other",
            labelEn: "Other",
            valueMr: "इतर",
            labelMr: "इतर",
          },
        ],
        required: true,
        messageEn: "Please select gender",
        messageMr: "कृपया लिंग निवडा",
      },
      {
        name: "deceasedName",
        labelEn: "Full Name of the Deceased",
        labelMr: "मृताचे पूर्ण नाव",
        required: true,
        messageEn: "Please enter deceased name",
        messageMr: "कृपया मृताचे नाव प्रविष्ट करा",
      },
      {
        name: "deceasedAadhar",
        type: "file",
        labelEn: "Aadhar Card of the Deceased",
        labelMr: "मृत व्यक्तीचे आधारकार्ड",
        required: true,
        messageEn: "Please upload Aadhar card of the deceased",
        messageMr: "कृपया मृत व्यक्तीचे आधारकार्ड अपलोड करा",
      },
      {
        name: "fatherName",
        labelEn: "Husband/Father's Name",
        labelMr: "मृताच्या पतीचे / वडिलांचे नाव",
        required: true,
        messageEn: "Please enter husband/father's name",
        messageMr: "कृपया पतीचे/वडिलांचे नाव प्रविष्ट करा",
      },
      {
        name: "motherName",
        labelEn: "Mother's Name",
        labelMr: "मृताच्या आईचे नाव",
        required: true,
        messageEn: "Please enter mother's name",
        messageMr: "कृपया आईचे नाव प्रविष्ट करा",
      },
      {
        name: "placeOfDeath",
        labelEn: "Place of Death of the Deceased",
        labelMr: "मयत व्यक्तीचा मृत्यूचे ठिकाण",
        required: true,
        messageEn: "Please enter place of death",
        messageMr: "कृपया मृत्यूचे ठिकाण प्रविष्ट करा",
      },
      {
        name: "permanentAddress",
        labelEn: "Permanent Address of the Deceased as per Aadhar Card",
        labelMr: "मयत व्यक्तीचा कायमचा पत्ता आधार कार्ड नुसार",
        required: true,
        messageEn: "Please enter permanent address",
        messageMr: "कृपया कायमचा पत्ता प्रविष्ट करा",
      },
      {
        name: "otherInfo",
        labelEn: "Other Information",
        labelMr: "इतर माहिती",
        required: false,
        messageEn: "Please enter other information",
        messageMr: "कृपया इतर माहिती प्रविष्ट करा",
      },
    ],
    "marriage-registration": [
      {
        name: "applicantName",
        labelEn: "Applicant Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "mobileNumber",
        labelEn: "Applicant Mobile No.",
        labelMr: "अर्जदाराचा मोबाईल नं",
        required: true,
        messageEn: "Please enter mobile number",
        messageMr: "कृपया मोबाईल नंबर प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Applicant Email ID",
        labelMr: "अर्जदाराचा इमेल आयडी",
        required: true,
        messageEn: "Please enter email ID",
        messageMr: "कृपया इमेल आयडी प्रविष्ट करा",
      },
      {
        name: "applicantAddress",
        labelEn: "Applicant Address",
        labelMr: "अर्जदाराचा पत्ता",
        required: true,
        messageEn: "Please enter applicant address",
        messageMr: "कृपया अर्जदाराचा पत्ता प्रविष्ट करा",
      },
      {
        name: "husbandName",
        labelEn: "Husband's Full Name",
        labelMr: "पतीचे पूर्ण नाव",
        required: true,
        messageEn: "Please enter husband's full name",
        messageMr: "कृपया पतीचे पूर्ण नाव प्रविष्ट करा",
      },
      {
        name: "wifeName",
        labelEn: "Wife's Full Name (Maher's)",
        labelMr: "पत्नीचे पूर्ण नाव (माहेर कडील)",
        required: true,
        messageEn: "Please enter wife's full name",
        messageMr: "कृपया पत्नीचे पूर्ण नाव प्रविष्ट करा",
      },
      {
        name: "marriageDate",
        type: "date",
        labelEn: "Marriage Date",
        labelMr: "विवाह दिनांक",
        required: true,
        messageEn: "Please enter marriage date",
        messageMr: "कृपया विवाह दिनांक प्रविष्ट करा",
      },
      {
        name: "registrationYear",
        labelEn: "Year Marriage Registered with Gram Panchayat",
        labelMr: "ग्रामपंचायतला विवाह नोंद केलेले वर्ष",
        required: true,
        messageEn: "Please enter registration year",
        messageMr: "कृपया नोंदणी वर्ष प्रविष्ट करा",
      },
      {
        name: "aadharCard",
        type: "file",
        labelEn: "Husband/Wife's Aadhar Card",
        labelMr: "पती / पत्नी चे आधार कार्ड",
        required: true,
        messageEn: "Please upload Aadhar card",
        messageMr: "कृपया आधार कार्ड अपलोड करा",
      },
      {
        name: "husbandPhoto",
        type: "file",
        labelEn: "Husband's Passport Photo",
        labelMr: "पतीचा पासपोर्ट फोटो",
        required: true,
        messageEn: "Please upload husband's passport photo",
        messageMr: "कृपया पतीचा पासपोर्ट फोटो अपलोड करा",
      },
      {
        name: "wifePhoto",
        type: "file",
        labelEn: "Wife's Passport Photo",
        labelMr: "पत्नीचा पासपोर्ट फोटो",
        required: true,
        messageEn: "Please upload wife's passport photo",
        messageMr: "कृपया पत्नीचा पासपोर्ट फोटो अपलोड करा",
      },
    ],
    "property-form": [
      {
        name: "applicantName",
        labelEn: "Applicant's name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Applicant's contact number",
        labelMr: "अर्जदाराचा संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "propertyNumber",
        labelEn:
          "Property number of the property for which Form No. 8 is required?",
        labelMr: "ज्या मिळकतीचा नमुना नं. ८ हवा आहे त्या मिळकतीचा क्रमांक?",
        required: true,
        messageEn: "Please enter property number",
        messageMr: "कृपया मिळकतीचा क्रमांक प्रविष्ट करा",
      },
      {
        name: "taxStatus",
        labelEn: "Has the tax on this property been paid or not?",
        labelMr: "सदर मिळकतीचा कर भरणा केला आहे किंवा नाही?",
        required: true,
        messageEn: "Please enter tax payment status",
        messageMr: "कृपया कर भरणा स्थिती प्रविष्ट करा",
      },
      {
        name: "taxReceipt",
        type: "file",
        labelEn: "Receipt of tax payment",
        labelMr: "कर भरणा केलेबाबतची पावती",
        required: true,
        messageEn: "Please upload tax payment receipt",
        messageMr: "कृपया कर भरणा पावती अपलोड करा",
      },
    ],
    "property-mutation": [
      {
        name: "applicantName",
        labelEn: "Applicant Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Contact Number",
        labelMr: "संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Email",
        labelMr: "ईमेल",
        required: true,
        messageEn: "Please enter email",
        messageMr: "कृपया ईमेल प्रविष्ट करा",
      },
      {
        name: "propertyHolderName",
        labelEn:
          "Name of the property holder to whom the change is to be registered",
        labelMr: "फेरफार नोंद करावयाच्या मिळकत धारकाचे नाव",
        required: true,
        messageEn: "Please enter property holder name",
        messageMr: "कृपया मिळकत धारकाचे नाव प्रविष्ट करा",
      },
      {
        name: "propertyNumber",
        labelEn: "Property Number",
        labelMr: "मिळकत क्रमांक",
        required: true,
        messageEn: "Please enter property number",
        messageMr: "कृपया मिळकत क्रमांक प्रविष्ट करा",
      },
      {
        name: "changeType",
        labelEn: "Type of change",
        labelMr: "फेरफार प्रकार",
        required: true,
        messageEn: "Please enter type of change",
        messageMr: "कृपया फेरफार प्रकार प्रविष्ट करा",
      },
      {
        name: "documents",
        type: "file",
        labelEn: "Required documents",
        labelMr: "आवश्यक दस्तऐवज",
        required: true,
        messageEn: "Please upload required documents",
        messageMr: "कृपया आवश्यक दस्तऐवज अपलोड करा",
      },
      {
        name: "otherDetails",
        labelEn: "Other required information details",
        labelMr: "इतर आवश्यक माहिती तपशील",
        required: false,
        messageEn: "Please enter other details if any",
        messageMr: "कृपया इतर माहिती तपशील प्रविष्ट करा",
      },
    ],
    "job-card-work": [
      {
        name: "familyHead",
        labelEn: "Name of the head of the family holding the job card",
        labelMr: "जॉब कार्ड धारक कुटुंबाचे कुटुंब प्रमुखाचे नाव",
        required: true,
        messageEn: "Please enter family head name",
        messageMr: "कृपया कुटुंब प्रमुखाचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Contact number",
        labelMr: "संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Email",
        labelMr: "ईमेल",
        required: true,
        messageEn: "Please enter email",
        messageMr: "कृपया ईमेल प्रविष्ट करा",
      },
      {
        name: "jobCardNumber",
        labelEn: "Job card number",
        labelMr: "जॉब कार्ड नंबर",
        required: true,
        messageEn: "Please enter job card number",
        messageMr: "कृपया जॉब कार्ड नंबर प्रविष्ट करा",
      },
      {
        name: "workStartDate",
        type: "date",
        labelEn: "Since when is work required?",
        labelMr: "कधीपासून काम हवे आहे?",
        required: true,
        messageEn: "Please enter work start date",
        messageMr: "कृपया काम सुरू करण्याची तारीख प्रविष्ट करा",
      },
      {
        name: "workersRequired",
        labelEn: "How many workers are required?",
        labelMr: "किती मजुरांना काम हवे आहे?",
        required: true,
        messageEn: "Please enter number of workers required",
        messageMr: "कृपया आवश्यक मजुरांची संख्या प्रविष्ट करा",
      },
      {
        name: "requisitionForm",
        type: "file",
        labelEn: "Attach a copy of the work requisition form",
        labelMr: "काम मागणी अर्जाची प्रत जोडा",
        required: true,
        messageEn: "Please upload work requisition form",
        messageMr: "कृपया काम मागणी अर्ज अपलोड करा",
      },
    ],
    "business-registration": [
      {
        name: "email",
        labelEn: "Email",
        labelMr: "इमेल",
        required: true,
        messageEn: "Please enter email",
        messageMr: "कृपया इमेल प्रविष्ट करा",
      },
      {
        name: "applicantName",
        labelEn: "Name of Applicant",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Contact Number",
        labelMr: "संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "businessDescription",
        labelEn: "Brief Description of Business to be Conducted",
        labelMr: "कोणता व्यवसाय करावयाचा आहे त्याचे थोडक्यात वर्णन",
        required: true,
        messageEn: "Please enter business description",
        messageMr: "कृपया व्यवसायाचे वर्णन प्रविष्ट करा",
      },
      {
        name: "propertyNumber",
        labelEn: "Number of Property in which Business to be Conducted",
        labelMr: "व्यवसाय ज्या मिळकतीत करावयाचा आहे तिचा क्रमांक",
        required: true,
        messageEn: "Please enter property number",
        messageMr: "कृपया मिळकतीचा क्रमांक प्रविष्ट करा",
      },
      {
        name: "propertyOwner",
        labelEn: "Who Owns the Property?",
        labelMr: "मिळकतीची मालकी कोणाची आहे?",
        required: true,
        messageEn: "Please enter property owner name",
        messageMr: "कृपया मिळकत मालकाचे नाव प्रविष्ट करा",
      },
      {
        name: "ownershipProof",
        type: "file",
        labelEn: "Proof of Ownership or Lease Agreement",
        labelMr: "मालकी हक्काचा पुरावा किंवा भाडेकरार",
        required: true,
        messageEn: "Please upload ownership proof or lease agreement",
        messageMr: "कृपया मालकी हक्काचा पुरावा किंवा भाडेकरार अपलोड करा",
      },
      {
        name: "additionalDocuments",
        type: "file",
        labelEn: "Other Required Evidence",
        labelMr: "इतर आवश्यक पुरावे",
        required: true,
        messageEn: "Please upload additional required documents",
        messageMr: "कृपया इतर आवश्यक पुरावे अपलोड करा",
      },
    ],
    "poverty-line": [
      {
        name: "applicantName",
        labelEn: "Applicant Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Applicant Contact Number",
        labelMr: "अर्जदाराचा संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Applicant Email",
        labelMr: "अर्जदाराचा ईमेल",
        required: true,
        messageEn: "Please enter email",
        messageMr: "कृपया ईमेल प्रविष्ट करा",
      },
      {
        name: "householdHead",
        labelEn: "Name of Head of Household in Poverty Line List",
        labelMr: "दारिद्र रेषा यादीतील कुटुंब प्रमुखाचे नाव",
        required: true,
        messageEn: "Please enter name of head of household",
        messageMr: "कृपया कुटुंब प्रमुखाचे नाव प्रविष्ट करा",
      },
      {
        name: "povertyLineNumber",
        labelEn: "Poverty Line Number",
        labelMr: "दारिद्र रेषा क्रमांक",
        required: true,
        messageEn: "Please enter poverty line number",
        messageMr: "कृपया दारिद्र रेषा क्रमांक प्रविष्ट करा",
      },
    ],
    "residence-certificate": [
      {
        name: "applicantName",
        labelEn: "Applicant Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "contactNumber",
        labelEn: "Applicant Contact Number",
        labelMr: "अर्जदाराचा संपर्क क्रमांक",
        required: true,
        messageEn: "Please enter contact number",
        messageMr: "कृपया संपर्क क्रमांक प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Applicant Email",
        labelMr: "अर्दाराचा ईमेल",
        required: true,
        messageEn: "Please enter email",
        messageMr: "कृपया ईमेल प्रविष्ट करा",
      },
      {
        name: "aadhaarNumber",
        labelEn: "Applicant Aadhaar Number",
        labelMr: "अर्जदाराचा आधार क्रमांक",
        required: true,
        messageEn: "Please enter Aadhaar number",
        messageMr: "कृपया आधार क्रमांक प्रविष्ट करा",
      },
      {
        name: "residenceProof",
        type: "file",
        labelEn: "Applicant Residence Proof",
        labelMr: "अर्जदाराचा रहिवाशी दर्शविणारा पुरावा",
        required: true,
        messageEn: "Please upload residence proof",
        messageMr: "कृपया रहिवाशी पुरावा अपलोड करा",
      },
    ],
    "birth-registration": [
      {
        name: "applicantName",
        labelEn: "Applicant's Name",
        labelMr: "अर्जदाराचे नाव",
        required: true,
        messageEn: "Please enter applicant name",
        messageMr: "कृपया अर्जदाराचे नाव प्रविष्ट करा",
      },
      {
        name: "mobileNumber",
        labelEn: "Applicant's Mobile No.",
        labelMr: "अर्जदाराचा मोबाईल नं",
        required: true,
        messageEn: "Please enter mobile number",
        messageMr: "कृपया मोबाईल नंबर प्रविष्ट करा",
      },
      {
        name: "email",
        labelEn: "Applicant's Email ID",
        labelMr: "अर्जदाराचा इमेल आयडी",
        required: true,
        messageEn: "Please enter email ID",
        messageMr: "कृपया इमेल आयडी प्रविष्ट करा",
      },
      {
        name: "applicantAddress",
        labelEn: "Applicant's Address",
        labelMr: "अर्जदाराचा पत्ता",
        required: true,
        messageEn: "Please enter applicant address",
        messageMr: "कृपया अर्जदाराचा पत्ता प्रविष्ट करा",
      },
      {
        name: "dateOfBirth",
        type: "date",
        labelEn: "Child's Date of Birth",
        labelMr: "बाळाची जन्म तारीख",
        required: true,
        messageEn: "Please enter date of birth",
        messageMr: "कृपया जन्म तारीख प्रविष्ट करा",
      },
      {
        name: "placeOfBirth",
        labelEn: "Place of Birth",
        labelMr: "जन्माचे ठिकाण",
        required: true,
        messageEn: "Please enter place of birth",
        messageMr: "कृपया जन्माचे ठिकाण प्रविष्ट करा",
      },
      {
        name: "gender",
        type: "select",
        labelEn: "Gender",
        labelMr: "लिंग",
        options: [
          {
            valueEn: "",
            labelEn: "-- Select --",
            valueMr: "",
            labelMr: "-- निवडा --",
          },
          {
            valueEn: "male",
            labelEn: "Male",
            valueMr: "पुरुष",
            labelMr: "पुरुष",
          },
          {
            valueEn: "female",
            labelEn: "Female",
            valueMr: "स्त्री",
            labelMr: "स्त्री",
          },
          {
            valueEn: "other",
            labelEn: "Other",
            valueMr: "इतर",
            labelMr: "इतर",
          },
        ],
        required: true,
        messageEn: "Please select gender",
        messageMr: "कृपया लिंग निवडा",
      },
      {
        name: "childName",
        labelEn: "Child's Name",
        labelMr: "बाळाचे नाव",
        required: true,
        messageEn: "Please enter child name",
        messageMr: "कृपया बाळाचे नाव प्रविष्ट करा",
      },
      {
        name: "fatherName",
        labelEn: "Child's Father's Name",
        labelMr: "बाळाच्या वडिलांचे नाव",
        required: true,
        messageEn: "Please enter father's name",
        messageMr: "कृपया वडिलांचे नाव प्रविष्ट करा",
      },
      {
        name: "fatherAadhar",
        labelEn: "Father's Aadhar Card Number",
        labelMr: "वडिलांचा आधारकार्ड नंबर",
        required: true,
        messageEn: "Please enter father's Aadhar number",
        messageMr: "कृपया वडिलांचा आधारकार्ड नंबर प्रविष्ट करा",
      },
      {
        name: "fatherAadharCopy",
        type: "file",
        labelEn: "Father's Aadhar Card Photocopy",
        labelMr: "वडिलांचा आधारकार्ड छायांकित प्रत",
        required: true,
        messageEn: "Please upload father's Aadhar card copy",
        messageMr: "कृपया वडिलांच्या आधारकार्डची छायांकित प्रत अपलोड करा",
      },
      {
        name: "motherName",
        labelEn: "Child's Mother's Name",
        labelMr: "बाळाच्या आईचे नाव",
        required: true,
        messageEn: "Please enter mother's name",
        messageMr: "कृपया आईचे नाव प्रविष्ट करा",
      },
      {
        name: "motherAadhar",
        labelEn: "Mother's Aadhar Card Number",
        labelMr: "आईचा आधारकार्ड नंबर",
        required: true,
        messageEn: "Please enter mother's Aadhar number",
        messageMr: "कृपया आईचा आधारकार्ड नंबर प्रविष्ट करा",
      },
      {
        name: "motherAadharCopy",
        type: "file",
        labelEn: "Mother's Aadhar Card Photocopy",
        labelMr: "आईचा आधारकार्ड छायांकित प्रत",
        required: true,
        messageEn: "Please upload mother's Aadhar card copy",
        messageMr: "कृपया आईच्या आधारकार्डची छायांकित प्रत अपलोड करा",
      },
      {
        name: "permanentAddress",
        labelEn: "Parents' Permanent Address",
        labelMr: "आई वडिलांचा कायमचा पत्ता",
        required: true,
        messageEn: "Please enter permanent address",
        messageMr: "कृपया कायमचा पत्ता प्रविष्ट करा",
      },
      {
        name: "addressAtBirth",
        labelEn: "Parents' Address at Time of Birth",
        labelMr: "आई वडिलांचा बाळाच्या जन्माच्या वेळीच पत्ता",
        required: true,
        messageEn: "Please enter address at time of birth",
        messageMr: "कृपया जन्माच्या वेळीचा पत्ता प्रविष्ट करा",
      },
    ],
  };

  // Service style cards
// SERVICES - English (literal translations of Marathi descriptions from the image)
const servicesDataEn = [
  {
    title: "Building Permit",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/construction-permit.pdf",
    status: "Online",
    fee: "₹50.00",
    processingTime: "7 days",
    formId: "construction-permit",
  },
  {
    title: "Birth Registration Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/birth-registration.pdf",
    status: "Online",
    fee: "₹30.00",
    processingTime: "5 days",
    formId: "birth-registration",
  },
  {
    title: "Death Registration Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/death-registration.pdf",
    status: "Online",
    fee: "₹30.00",
    processingTime: "5 days",
    formId: "death-registration",
  },
  {
    title: "Marriage Registration Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/marriage-registration.pdf",
    status: "Online",
    fee: "₹100.00",
    processingTime: "10 days",
    formId: "marriage-registration",
  },
  {
    title: "Form No. 08 Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/form-08.pdf",
    status: "Online",
    fee: "₹20.00",
    processingTime: "3 days",
    formId: "property-form",
  },
  {
    title: "Change Registration Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/change-registration.pdf",
    status: "Online",
    fee: "₹50.00",
    processingTime: "7 days",
    formId: "property-mutation",
  },
  {
    title: "Form No. 04 Work Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/form-04-work.pdf",
    status: "Online",
    fee: "₹25.00",
    processingTime: "5 days",
    formId: "job-card-work",
  },
  {
    title: "Business No Objection Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/business-noc.pdf",
    status: "Online",
    fee: "₹200.00",
    processingTime: "15 days",
    formId: "business-registration",
  },
  {
    title: "Poverty Line Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/poverty-line.pdf",
    status: "Online",
    fee: "₹0.00",
    processingTime: "10 days",
    formId: "poverty-line",
  },
  {
    title: "Residence Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/residence-certificate.pdf",
    status: "Online",
    fee: "₹50.00",
    processingTime: "7 days",
    formId: "residence-certificate",
  },
  {
    title: "Form No. 04 Work Request Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/form-04-request.pdf",
    status: "Online",
    fee: "₹25.00",
    processingTime: "5 days",
    formId: "job-card-work",
  },
  {
    title: "Business No Objection Certificate Application",
    description:
      "A resident self-declaration form means the official record of your residence information and its related details.",
    link: "/files/business-noc-2.pdf",
    status: "Online",
    fee: "₹200.00",
    processingTime: "15 days",
    formId: "business-registration",
  },
];

// SERVICES - Marathi (exact from image)
const servicesDataMr = [
  {
    title: "बांधकाम परवानगी",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/construction-permit.pdf",
    status: "ऑनलाइन",
    fee: "₹50.00",
    processingTime: "7 दिवस",
    formId: "construction-permit",
  },
  {
    title: "जन्म नोंद दाखला मागणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/birth-registration.pdf",
    status: "ऑनलाइन",
    fee: "₹30.00",
    processingTime: "5 दिवस",
    formId: "birth-registration",
  },
  {
    title: "मृत्यू नोंद दाखला मागणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/death-registration.pdf",
    status: "ऑनलाइन",
    fee: "₹30.00",
    processingTime: "5 दिवस",
    formId: "death-registration",
  },
  {
    title: "विवाह नोंदणी दाखल्यासाठी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/marriage-registration.pdf",
    status: "ऑनलाइन",
    fee: "₹100.00",
    processingTime: "10 दिवस",
    formId: "marriage-registration",
  },
  {
    title: "नमुना नं ०८ मागणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/form-08.pdf",
    status: "ऑनलाइन",
    fee: "₹20.00",
    processingTime: "3 दिवस",
    formId: "property-form",
  },
  {
    title: "फेरफार नोंदणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/change-registration.pdf",
    status: "ऑनलाइन",
    fee: "₹50.00",
    processingTime: "7 दिवस",
    formId: "property-mutation",
  },
  {
    title: "नमुना नं. ०४ काम मागणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/form-04-work.pdf",
    status: "ऑनलाइन",
    fee: "₹25.00",
    processingTime: "5 दिवस",
    formId: "job-card-work",
  },
  {
    title: "व्यवसाय ना हरकत दाखला अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/business-noc.pdf",
    status: "ऑनलाइन",
    fee: "₹200.00",
    processingTime: "15 दिवस",
    formId: "business-registration",
  },
  {
    title: "दारिद्रय रेषा दाखला मागणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/poverty-line.pdf",
    status: "ऑनलाइन",
    fee: "₹0.00",
    processingTime: "10 दिवस",
    formId: "poverty-line",
  },
  {
    title: "रहिवाशी दाखला मागणी अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/residence-certificate.pdf",
    status: "ऑनलाइन",
    fee: "₹50.00",
    processingTime: "7 दिवस",
    formId: "residence-certificate",
  },
  // {
  //   title: "नमुना नं. ०४ काम मागणी अर्ज",
  //   description:
  //     "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
  //   link: "/files/form-04-request.pdf",
  //   status: "ऑनलाइन",
  //   fee: "₹25.00",
  //   processingTime: "5 दिवस",
  //   formId: "job-card-work",
  // },
  {
    title: "व्यवसाय ना हरकत दाखला अर्ज",
    description:
      "रहिवाशी स्वयंघोषणापत्र म्हणजे आपल्या निवासस्थानाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
    link: "/files/business-noc-2.pdf",
    status: "ऑनलाइन",
    fee: "₹200.00",
    processingTime: "15 दिवस",
    formId: "business-registration",
  },
];

  // English document data
  const documentDataEn = [
    {
      title: "Resident Self-Declaration Form",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/resident-self-declaration-form.pdf",
      sampleFormLink: "/assets/forms/sample-resident-self-declaration-form.pdf",
    },
    {
      title: "Electricity Connection Self-Declaration Form",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/electricity-connection-form.pdf",
      sampleFormLink: "/assets/forms/sample-electricity-connection-form.pdf",
    },
    {
      title: "Self-Declaration Form for Toilet",
      description:
        "A self-declaration form for a residence with a toilet is an official record of the location and related details.",
      link: "/assets/forms/self-declaration-form-for-toilet.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-toilet.pdf",
    },
    {
      title: "Self-Declaration of Existence",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/self-declaration-of-existence.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-of-existence.pdf",
    },
    {
      title: "Self-Declaration Form Regarding Being a Widow",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/self-declaration-form-for-widow.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-widow.pdf",
    },
    {
      title: "Self-Declaration Form Regarding Being Abandoned",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/self-declaration-form-for-abandoned.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-abandoned.pdf",
    },
    {
      title: "Self-declaration form if there is a split family",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/self-declaration-form-for-split-family.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-split-family.pdf",
    },
    {
      title: "Self-declaration of not availing any scheme",
      description:
        "A resident self-declaration form is an official record of your residence information and relevant details.",
      link: "/assets/forms/self-declaration-of-not-availing-scheme.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-of-not-availing-scheme.pdf",
    },
  ];

  // Marathi document data
  const documentDataMr = [
    {
      title: "रहिवासी स्व-घोषणा प्रपत्र",
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/resident-self-declaration-form.pdf",
      sampleFormLink: "/assets/forms/sample-resident-self-declaration-form.pdf",
    },
    {
      title: "वीज कनेक्शन स्व-घोषणा प्रपत्र",
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/electricity-connection-form.pdf",
      sampleFormLink: "/assets/forms/sample-electricity-connection-form.pdf",
    },
    {
      title: "शौचालय असल्याबाबत स्वयंघोषणापत्र",
      description:
        "शौचालय असलेल्या निवासस्थानासाठी स्वयंघोषणापत्र म्हणजे त्या ठिकाणाची माहिती आणि संबंधित तपशीलांची अधिकृत नोंद.",
      link: "/assets/forms/self-declaration-form-for-toilet.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-toilet.pdf",
    },
    {
      title: "हयातअसल्याबाबत स्वयंघोषणापत्र", 
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/self-declaration-of-existence.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-of-existence.pdf",
    },
    {
      title: "विधवा असल्याबाबत स्वयंघोषणापत्र",
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/self-declaration-form-for-widow.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-widow.pdf",
    },
    {
      title: "परित्यक्त्या असल्याबाबत स्वयंघोषणापत्र",
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/self-declaration-form-for-abandoned.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-abandoned.pdf",
    },
    {
      title: "विभक्त कुटुंब असल्यास स्वयंघोषणापत्र",
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/self-declaration-form-for-split-family.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-form-for-split-family.pdf",
    },
    {
      title: "कोणत्याही योजनेचा लाभ न घेतल्याचे स्वयंघोषणापत्र",
      description:
        "रहिवासी स्व-घोषणा प्रपत्र म्हणजे तुमच्या निवास माहिती आणि संबंधित तपशीलांचा अधिकृत रेकॉर्ड आहे.",
      link: "/assets/forms/self-declaration-of-not-availing-scheme.pdf",
      sampleFormLink: "/assets/forms/sample-self-declaration-of-not-availing-scheme.pdf",
    },
  ];

  // English application data
  const applicationDataEn = [
    {
      title: "Application for Birth and Death Registration Certificate",
      description:
        "Apply for birth or death registration certificate. Required for official records and legal documentation.",
      link: "/assets/forms/application-birth-death-registration.pdf",
      sampleFormLink: "/assets/forms/sample-birth-death-registration.pdf",
    },
    {
      title: "Marriage Notification for Marriage Registration",
      description:
        "Submit marriage registration notification and obtain official marriage certificate.",
      link: "/assets/forms/marriage-notification-registration.pdf",
      sampleFormLink: "/assets/forms/sample-marriage-notification.pdf",
    },
    {
      title: "Application for Extract No. 8",
      description:
        "Request Extract No. 8 document for property and legal purposes.",
      link: "/assets/forms/construction-permit.pdf",
      sampleFormLink: "/assets/forms/sample-construction-permit.pdf",
    },
    {
      title: "Application for Extract No. 8",
      description:
        "Request Extract No. 8 document for property and legal purposes.",
      link: "/assets/forms/rti-application-form.pdf",
      sampleFormLink: "/assets/forms/sample-rti-application-form.pdf",
    },
  ];

  // Marathi application data
  const applicationDataMr = [
    {
      title: "जन्म आणि मृत्यू नोंदणी प्रमाणपत्रासाठी अर्ज",
      description:
        "जन्म किंवा मृत्यू नोंदणी प्रमाणपत्रासाठी अर्ज करा. अधिकृत नोंदी आणि कायदेशीर दस्तऐवजीकरणासाठी आवश्यक.",
      link: "/assets/forms/application-birth-death-registration.pdf",
      sampleFormLink: "/assets/forms/sample-birth-death-registration.pdf",
    },
    {
      title: "विवाह नोंदणीसाठी विवाह अधिसूचना",
      description:
        "विवाह नोंदणी अधिसूचना सादर करा आणि अधिकृत विवाह प्रमाणपत्र प्राप्त करा.",
      link: "/assets/forms/marriage-notification-registration.pdf",
      sampleFormLink: "/assets/forms/sample-marriage-notification.pdf",
    },
    {
      title: "बांधकाम परवानगी अर्ज",
      description:
        "मालमत्ता आणि कायदेशीर उद्देशांसाठी अर्क क्रमांक ८ दस्तऐवज विनंती करा.",
      link: "/assets/forms/construction-permit.pdf",
      sampleFormLink: "/assets/forms/sample-construction-permit.pdf",
    },
    {
      title: "माहितीचा अधिकार",
      description:
        "मालमत्ता आणि कायदेशीर उद्देशांसाठी अर्क क्रमांक ८ दस्तऐवज विनंती करा.",
      link: "/assets/forms/rti-application-form.pdf",
      sampleFormLink: "/assets/forms/rti-application-form.pdf",
    },
  ];

  // English certificate data (from the image)
  const certificateDataEn = [
    {
      title: "Caste Certificate",
      description:
        "Official document certifying the caste category of an individual for reservation and other benefits.",
      link: "/assets/forms/caste-form.pdf",
      sampleFormLink: "/assets/forms/sample-caste-form.pdf",
    },
    {
      title: "Small Landholding Farmer Certificate",
      description:
        "Certificate for farmers with small agricultural land holdings to avail government schemes and benefits.",
      link: "/assets/forms/small-landholding-farmer-form.pdf",
      sampleFormLink: "/assets/forms/sample-small-landholding-farmer-form.pdf",
    },
    {
      title: "Income and Assets Certificate",
      description:
        "Official document stating the annual income and assets of an individual or family for various purposes.",
      link: "/assets/forms/income-assets-form.pdf",
      sampleFormLink: "/assets/forms/sample-income-assets-form.pdf",
    },
    {
      title: "Other Income Certificate for Loan",
      description:
        "Certificate for other sources of income required for loan applications and financial assistance.",
      link: "/assets/forms/other-income-form-loan.pdf",
      sampleFormLink: "/assets/forms/sample-other-income-form-loan.pdf",
    },
  ];

  // Marathi certificate data (from the image)
  const certificateDataMr = [
    {
      title: "जातीचा दाखला",
      description:
        "आरक्षण आणि इतर फायद्यांसाठी व्यक्तीच्या जाती श्रेणीचे प्रमाणीकरण करणारे अधिकृत दस्तऐवज.",
      link: "/assets/forms/caste-form.pdf",
      sampleFormLink: "/assets/forms/sample-caste-form.pdf",
    },
    {
      title: "अल्पभूधारक शेतकरी",
      description:
        "सरकारी योजना आणि फायदे मिळवण्यासाठी छोट्या शेतजमीन धारकांसाठी प्रमाणपत्र.",
      link: "/assets/forms/small-landholding-farmer-form.pdf",
      sampleFormLink: "/assets/forms/sample-small-landholding-farmer-form.pdf",
    },
    {
      title: "यश व आर्थिक प्रमाणपत्र",
      description:
        "विविध हेतूंसाठी व्यक्ती किंवा कुटुंबाचे वार्षिक उत्पन्न आणि मालमत्ता दर्शविणारे अधिकृत दस्तऐवज.",
      link: "/assets/forms/income-assets-form.pdf",
      sampleFormLink: "/assets/forms/sample-income-assets-form.pdf",
    },
    {
      title: "अन्य उत्पन्न प्रमाणपत्र कर्ज",
      description:
        "कर्ज अर्ज आणि आर्थिक सहाय्यासाठी आवश्यक असलेल्या इतर उत्पन्न स्रोतांचे प्रमाणपत्र.",
      link: "/assets/forms/other-income-form-loan.pdf",
      sampleFormLink: "/assets/forms/sample-other-income-form-loan.pdf",
    },
  ];

  // Choose data arrays based on current language
  const currentDocumentData =
    i18n.language === "en" ? documentDataEn : documentDataMr;
  const currentCertificateData =
    i18n.language === "en" ? certificateDataEn : certificateDataMr;

  // Merge application and services data with a type indicator
  const currentApplicationData = [
    ...(i18n.language === "en" ? applicationDataEn : applicationDataMr).map(
      (item) => ({
        ...item,
        type: "pdf",
      })
    ),
    ...(i18n.language === "en" ? servicesDataEn : servicesDataMr).map(
      (item) => ({
        ...item,
        type: "online",
      })
    ),
  ];

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* self declaration form */}
        <div className="border rounded-md p-4 bg-white flex flex-col gap-2">
          {/* title */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-3 items-center">
              <img
                className="w-10 h-10 relative"
                src={formIcon}
                alt="Form Icon"
              />
              <h2 className="font-semibold w-full h-fit text-2xl max-sm:text-xl m-0">
                {t("Self-Declaration Form") || "स्व-घोषणा प्रपत्र"}
              </h2>
            </div>
            <p className="text-base font-normal">
              {t(
                "Explore our ongoing development initiatives, completed projects, and government schemes designed to improve rural infrastructure and community welfare."
              ) ||
                "येथे तुम्हाला आवश्यक असलेले सर्व प्रकारचे स्व: घोषणा पत्र मिळतील, जसे की रहिवासी दाखला, शौचालय दाखला, विधवा असल्याबाबत दाखला, आणि इतर."}
            </p>
          </div>

          {/* document cards */}
          <div className="grid max-sm:grid-cols-1 max-md:grid-cols-2 max-lg:grid-cols-3 grid-cols-4 gap-4 w-full">
            {currentDocumentData.map((doc, index) => (
              <DocumentCard
                key={index}
                title={doc.title}
                description={doc.description}
                downloadLink={doc.link}
                sampleFormLink={doc.sampleFormLink}
              />
            ))}
          </div>
        </div>

        {/* Application forms */}

        <div className="border rounded-md p-4 bg-white flex flex-col gap-2">
          {/* title */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-3 items-center">
               <img
                className="w-10 h-10 relative"
                style={{
                  background: "linear-gradient(135deg, #00C950 0%, #006D29 100%)",
                  padding: "8px",
                  border: "1px",
                  borderRadius: '4px'
                }}
                src={articleIcon}
                alt="application Icon"
              />
              <h2 className="font-semibold w-full h-fit text-2xl max-sm:text-xl m-0">
                {t("Application") || "अर्ज"}
              </h2>
            </div>
            <p className="text-base font-normal">
              {t(
                "Explore our ongoing development initiatives, completed projects, and government schemes designed to improve rural infrastructure and community welfare."
              ) ||
                "आमच्या चालू विकास उपक्रमांचे, पूर्ण केलेल्या प्रकल्पांचे आणि ग्रामीण पायाभूत सुविधा व समुदाय कल्याण सुधारण्यासाठी तयार केलेल्या सरकारी योजनांचे अन्वेषण करा."}
            </p>
          </div>

          {/* Application cards (both PDF and Online) */}
          <div className="grid max-sm:grid-cols-1 max-md:grid-cols-2 max-lg:grid-cols-3 grid-cols-4 gap-4 w-full">
            {currentApplicationData.map((app, index) => (
              <DocumentCard
                key={index}
                title={app.title}
                description={app.description}
                downloadLink={app.type === "pdf" ? app.link : undefined}
                sampleFormLink={app.type === "pdf" ? app.sampleFormLink : undefined}
                status={app.type === "online" ? app.status : undefined}
                fee={app.type === "online" ? app.fee : undefined}
                processingTime={
                  app.type === "online" ? app.processingTime : undefined
                }
                infoLabel={
                  app.type === "online"
                    ? i18n.language === "mr"
                      ? "अधिक माहिती"
                      : "More info"
                    : undefined
                }
                actionLabel={
                  app.type === "online"
                    ? i18n.language === "mr"
                      ? "ऑनलाइन अर्ज भरा"
                      : "Apply online"
                    : undefined
                }
                showServiceButtons={app.type === "online"}
                hideIcon={app.type === "online"}
                onActionClick={
                  app.type === "online"
                    ? () => {
                        setSelectedService(app);
                        setModalOpen(true);
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </div>

        <div className="border rounded-md p-4 bg-white flex flex-col gap-2">
          {/* title */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-3 items-center">
              <img
                className="w-10 h-10 relative"
                style={{
                  background:
                    "linear-gradient(135deg, #2B7FFF 0%, #002371 100%)",
                  padding: "8px",
                  border: "1px",
                  borderRadius: '4px'
                }}
                src={articleIcon}
                alt="application Icon"
              />
              <h2 className="font-semibold w-full h-fit text-2xl max-sm:text-xl m-0">
                {t("Certificate") || "प्रमाणपत्र"}
              </h2>
            </div>
            <p className="text-base font-normal">
              {t(
                "Access various certificates and official documents required for government schemes, educational purposes, and administrative procedures."
              ) ||
                "सरकारी योजना, शैक्षणिक हेतू आणि प्रशासकीय प्रक्रियेसाठी आवश्यक असलेली विविध प्रमाणपत्रे आणि अधिकृत कागदपत्रे मिळवा."}
            </p>
          </div>

          {/* certificate cards */}
          <div className="grid max-sm:grid-cols-1 max-md:grid-cols-2 max-lg:grid-cols-3 grid-cols-4 gap-4 w-full">
            {currentCertificateData.map((cert, index) => (
              <DocumentCard
                key={index}
                title={cert.title}
                description={cert.description}
                downloadLink={cert.link}
                sampleFormLink={cert.sampleFormLink}
              />
            ))}
          </div>
        </div>
              </div>

              {/* Application Form Modal */}
      <ApplicationFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedService(null);
        }}
        amount={selectedService?.fee}
        title={selectedService?.title}
        formConfig={
          selectedService?.formId ? formConfigs[selectedService.formId] : []
        }
      />
    </>
  );
};

export default DocumentDownload;
